
import { namespace } from 'vuex-class';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';

import {
	GetSingleStepTeamEventsDocument,
	GetSingleStepTeamEventsQuery,
	GetSingleStepTeamEventsQueryVariables,
} from '@/pages/graphql/queries/GetSingleStepTeamEvents.query';

import { SingleStepContext } from '@/helpers/sports/config';
import {
	SportEventBucketsEdgeV2,
	SportEventMatrixCompetitionColumn,
	SportEventMatrixCompetitionColumns,
	SportEventMatrixPackageRow,
} from '@/@types/graphql-types';

import CompetitionEventsItem from '@/components/sports/common/overview/calendar/CompetitionEventsItem.vue';
import CompetitionLogo from '@/components/sports/common/competition/logos/CompetitionLogo.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { Route } from 'vue-router';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';

const Language = namespace('language');
const Routing = namespace('routing');

@Component({
	name: 'TeamCompetitionsCalendar',
	components: {
		CompetitionEventsItem,
		CompetitionLogo,
		HiddenHorizontalScrollbar,
		ProviderIcon,
	},
	apollo: {
		competitionColumns: {
			query: GetSingleStepTeamEventsDocument,
			result() {
				if (this.competitionColumns.length === 0) return;
				this.$emit('mounted', [this.competitionColumns[0].competition.id]);
				this.$emit('competitions', this.competitionColumns);
			},
			variables(): GetSingleStepTeamEventsQueryVariables {
				return this.queryVariables;
			},
			update(data: GetSingleStepTeamEventsQuery) {
				return (data.sportEventMatrix as SportEventMatrixCompetitionColumns).competitions;
			},
		},
	},
})
export default class TeamCompetitionsCalendar extends Vue {
	faChevronUp = faChevronUp;
	faChevronDown = faChevronDown;

	@Prop({ required: true }) context: SingleStepContext;
	@Prop({ required: true }) competitionEvents: SportEventBucketsEdgeV2[];

	// DATA FETCHING
	@Prop({ required: true }) startAt: { from: string; to?: string };
	@Prop({ required: true }) competitor: { id: string; content: { name: string; iconUrl: string } };

	@Language.Getter country: string;
	@Language.Getter language: string;
	@Routing.State activeRoute: Route;
	competitionColumns: SportEventMatrixCompetitionColumn[] = [];

	unfilteredCompetitionEvents: SportEventBucketsEdgeV2[] = [];

	hasStoredInitialEvents = false;

	@Watch('competitionEvents', { immediate: true, deep: true })
	onCompetitionEventsChange(events: SportEventBucketsEdgeV2[]) {
		if (!this.hasStoredInitialEvents && events.length) {
			this.unfilteredCompetitionEvents = [...events];
			this.hasStoredInitialEvents = true;
		}
	}
	@Watch('activeRoute')
	onRouteChange() {
		this.hasStoredInitialEvents = false;
	}

	get competitionColumnsFromEvents() {
		return this.unfilteredCompetitionEvents
			.filter(({ key, node }) => !!node.edges?.length && key.competition != null)
			.map(({ key, node }) => ({ competition: key.competition, count: node.edges?.length }))
			.filter(el => !!el);
	}

	get queryVariables(): GetSingleStepTeamEventsQueryVariables {
		return {
			language: this.language,
			country: this.country,
			sport: this.context.sportType,
			filter: {
				startAt: this.startAt,
				competitorIds: [this.competitor.id],
			},
		};
	}

	// FILTERING
	@Prop({ required: true }) selectedCompetitions: string[];

	showProviderFilters = false;

	toggleProviderFilters() {
		this.showProviderFilters = !this.showProviderFilters;
		this.$emit('toggleShowProviderFilters');
	}

	get selectedCompetitionsPackages() {
		const packageList = this.competitionColumns
			.filter(({ competition }) => this.selectedCompetitions.includes(competition.id))
			.flatMap(({ packages }) => packages)
			// if multiple competitions are available on the same provider,
			// combine the providers into one with a totalled match count
			.reduce((list: Record<number, SportEventMatrixPackageRow>, row) => {
				if (!list[row.package.packageId]) {
					list[row.package.packageId] = { ...row };
				} else {
					list[row.package.packageId].count += row.count;
				}

				return list;
			}, {});

		return Object.values(packageList);
	}
}
